/**
 * The market's own tracking tags, injected only after the visitor has agreed to them.
 *
 * Each region carries a list of these in the CMS - none, one, or a dozen - so a market
 * can run its own analytics and its own marketing tags without a frontend deploy, and
 * without any of it leaking into another market. Two things about that list are load
 * bearing:
 *
 * - **Every tag names a consent category**, and nothing is injected until that category
 *   is granted. Not injected-and-disabled: `consent.ts` rule 1 is that nothing runs
 *   before the answer, and "loaded but told not to collect" is a much harder thing to
 *   explain to four data protection authorities than "not loaded".
 * - **Every tag names a placement.** `head` and `bodyEnd` are what vendors' instructions
 *   say, and some tags genuinely care - a script that expects the document to exist under
 *   it, a `<noscript>` pixel that is conventionally last in the body.
 *
 * ## The exception, and it is a real one
 *
 * A Tag Manager container may be marked **`loadBeforeConsent`**, and then it loads on
 * arrival with every Consent Mode signal denied, hearing the visitor's answer when it
 * comes. This is the "loaded but told not to collect" shape that the paragraph above
 * argues against, and it is here because the client asked for it after that argument was
 * put to them: it is how Google documents Consent Mode, how tag managers are normally
 * installed, and it is what lets GTM record a conversion that happens before the banner
 * is answered.
 *
 * What it costs is worth being precise about rather than reassuring about. A third party
 * runs code on the page before the visitor has said anything. Google's denied state means
 * no cookies and redacted pings rather than no requests at all, so the visitor's IP
 * address and page URL reach Google either way, and whether that is lawful in these four
 * markets is a question for the client's lawyers and not something this file settles.
 * Every other tag - GA4, and every pasted snippet - still waits, and `snippetsOf` refuses
 * to honour the flag for anything but a container, so the door is exactly this wide.
 *
 * It is off by default, per market, and a market that never ticks it behaves as before.
 *
 * ## Two honest limitations
 *
 * **Withdrawing consent cannot un-run a script.** What it can do is remove the nodes, so
 * nothing further loads through them, and drop the cookies - which is what happens here
 * and in `analytics.ts`. A visitor who withdraws mid-session and wants certainty should
 * reload, and after a reload nothing is injected at all until they say yes again.
 *
 * **A `<noscript>` fallback in one of these snippets does nothing.** This is a
 * single-page app: with scripting off there is no page to put a pixel on, because there
 * is no page. Pasting the vendor's full block is still right - it is what they support -
 * but the `<noscript>` half of it will never fire.
 *
 * ## Why this executes HTML from the CMS
 *
 * `createContextualFragment` builds *executable* script elements, unlike `innerHTML`,
 * which is the only reason a pasted vendor snippet works at all. That makes anyone who
 * can edit a `Region` able to run JavaScript on every page of that market - which is what
 * a tag manager field is, in every CMS that has one. It is why tracking codes live on
 * `Region` and not on anything a regional editor can open: the seeded editor roles grant
 * nothing on `Region`, and `check:roles` fails if that ever changes.
 */
import { dropCookies } from './consent';
import { gtag, pushConsentDefaults, updateConsent, type ConsentGrants } from './gtag';

export type TagPlacement = 'head' | 'bodyEnd';
export type TagConsentCategory = 'analytics' | 'marketing';

export interface TrackingCode {
  id: string;
  label: string;
  provider: 'ga4' | 'gtm' | 'custom';
  measurementId: string | null;
  containerId: string | null;
  snippet: string | null;
  placement: TagPlacement;
  consentCategory: TagConsentCategory;
  /** Tag Manager only: load the container up front and govern its tags with Consent Mode. */
  loadBeforeConsent: boolean;
}

export type { ConsentGrants };

/** What is on the page right now, so it can be taken off again. */
const injected = new Map<string, ChildNode[]>();

/**
 * The GA4 tag, if this market has one.
 *
 * GA4 is not injected as a snippet even though it comes as one, because the snippet
 * measures a single-page app exactly once: it sends the page view it was loaded for and
 * then never hears about a route change again. `analytics.ts` runs it properly - Consent
 * Mode, and a page view per navigation - so it needs the id rather than the script.
 */
export const ga4Of = (codes: TrackingCode[]): TrackingCode | null =>
  codes.find((c) => c.provider === 'ga4' && c.measurementId) ?? null;

/** The Tag Manager container, if this market has one. */
export const gtmOf = (codes: TrackingCode[]): TrackingCode | null =>
  codes.find((c) => c.provider === 'gtm' && c.containerId) ?? null;

/**
 * Everything that is a pasted snippet, in the order the CMS lists it.
 *
 * **`loadBeforeConsent` is not honoured here, and that is deliberate.** The field exists
 * because Strapi cannot show it on one provider and hide it on the others; a snippet is
 * arbitrary code from an arbitrary vendor and there is no Consent Mode governing it, so
 * "always on" for one of these would just be "runs without asking". Only a Tag Manager
 * container, which has a documented denied state, is allowed through that door.
 */
export const snippetsOf = (codes: TrackingCode[]): TrackingCode[] =>
  codes.filter((c) => c.provider !== 'ga4' && c.provider !== 'gtm'
    && c.snippet && c.snippet.trim() !== '');

function insert(code: TrackingCode) {
  if (injected.has(code.id)) return;

  const container = code.placement === 'bodyEnd' ? document.body : document.head;
  if (!container) return;

  try {
    const range = document.createRange();
    range.selectNodeContents(container);
    const fragment = range.createContextualFragment(code.snippet ?? '');
    const nodes = Array.from(fragment.childNodes);

    // Stamped so a human looking at the DOM can tell which CMS entry put this here, and
    // so a half-removed tag is visible rather than mysterious.
    for (const node of nodes) {
      if (node instanceof HTMLElement) node.dataset.wecoTag = code.id;
    }

    container.appendChild(fragment);
    injected.set(code.id, nodes);
  } catch (error) {
    // A snippet an administrator pasted badly must not take the site down with it. This
    // is the one place where CMS content can throw during a render path.
    console.error(`[tags] "${code.label}" could not be inserted`, error);
  }
}

function remove(id: string) {
  const nodes = injected.get(id);
  if (!nodes) return;
  for (const node of nodes) node.remove();
  injected.delete(id);
}

const GTM_SCRIPT_ID = 'weco-gtm';

/**
 * Google Tag Manager, from its container id.
 *
 * Loaded from the id rather than pasted as a snippet for the same reason GA4 is: the
 * order matters and a pasted block cannot guarantee it. Consent Mode defaults have to be
 * in `dataLayer` **before** the container script, or the container starts in an unknown
 * state, and `lib/gtag.ts` is what makes that ordering a fact rather than a hope.
 */
function ensureGtm(containerId: string) {
  if (document.getElementById(GTM_SCRIPT_ID)) return;

  pushConsentDefaults();
  gtag('js', new Date());

  const script = document.createElement('script');
  script.id = GTM_SCRIPT_ID;
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(containerId)}`;
  document.head.appendChild(script);
}

/**
 * The consent signals a container runs on, whether or not it is loaded yet.
 *
 * Pushed even when there is no container: `dataLayer` is shared, GA4 reads the same
 * signals, and a decision recorded before any Google tag arrives is one the tag picks up
 * on the way in. That is what `wait_for_update` is for.
 */
function applyConsentMode(codes: TrackingCode[], granted: ConsentGrants) {
  const gtm = gtmOf(codes);
  if (!gtm && !ga4Of(codes)) return;

  // Always-on loads the container now, in the fully denied state, and it hears the answer
  // when it comes - the one place on the site where a third party runs before the visitor
  // has said anything, see the module comment. Otherwise it waits like everything else.
  if (gtm && (gtm.loadBeforeConsent || granted[gtm.consentCategory])) {
    ensureGtm(gtm.containerId as string);
  }

  updateConsent(granted);
}

/**
 * Bring the page into line with a consent decision.
 *
 * Called on every change including the first, and safe to call repeatedly: a tag already
 * on the page is left alone rather than injected twice, which for an analytics tag would
 * mean counting everything twice.
 */
export function applyTrackingCodes(codes: TrackingCode[], granted: ConsentGrants) {
  applyConsentMode(codes, granted);

  const wanted = new Set<string>();

  for (const code of snippetsOf(codes)) {
    if (granted[code.consentCategory]) {
      wanted.add(code.id);
      insert(code);
    }
  }

  // Anything on the page that is no longer wanted - consent withdrawn, or the entry
  // removed in the CMS while the tab was open.
  for (const id of [...injected.keys()]) if (!wanted.has(id)) remove(id);
}

/**
 * Everything this module put on the page, gone, and the cookies with it.
 *
 * The cookie list is the common marketing set. It cannot be complete - a tag may set
 * anything it likes - and the honest answer to a visitor who wants certainty is a
 * reload, after which nothing is injected until they agree again.
 */
export function clearTrackingCodes() {
  for (const id of [...injected.keys()]) remove(id);
  dropCookies(['_fbp', '_fbc', 'fr', '_gcl_au', '_uetsid', '_uetvid', 'li_sugr', '_hjSessionUser']);
}

/** For tests and for the browser console: what is on the page and why. */
export const injectedTagIds = () => [...injected.keys()];
